import { DashboardOverview, ParkingLot, ParkingLotStats, FeedbackSummary, FeedbackActivityResponse } from './types';

// Function to generate paginated parking lots data
export const generatePaginatedParkingLots = (page: number = 1, limit: number = 50, search: string = '', sortBy: string = 'name', sortOrder: 'asc' | 'desc' = 'asc') => {
    // Generate 1500+ parking lots for realistic testing
    const totalParkingLots: ParkingLot[] = [];
    
    const parkingTypes = ['garage', 'underground', 'surface', 'street', 'park_and_ride'];
    const owners = ['City of Oslo', 'Bane NOR', 'Aker Brygge AS', 'Oslo Municipality', 'Private Owner', 'Shopping Center AS'];
    const areas = ['Downtown', 'Central', 'Aker Brygge', 'Grünerløkka', 'Frogner', 'St. Hanshaugen', 'Sagene', 'Gamle Oslo'];
    
    for (let i = 1; i <= 1500; i++) {
        const area = areas[i % areas.length];
        const parkingType = parkingTypes[i % parkingTypes.length];
        const owner = owners[i % owners.length];
        const spaces = Math.floor(Math.random() * 200) + 20; // 20-220 spaces
        
        const parkingLot: ParkingLot = {
            _id: i.toString(),
            lotId: `LOT${i.toString().padStart(3, '0')}`,
            name: `${area} Parking ${parkingType.charAt(0).toUpperCase() + parkingType.slice(1)} ${i}`,
            geometry: {
                type: 'Point',
                coordinates: [10.7522 + (Math.random() - 0.5) * 0.1, 59.9139 + (Math.random() - 0.5) * 0.1]
            },
            spaces,
            parking_type: parkingType,
            address: `${area} Street ${i}, Oslo`,
            owner,
            notes: `${parkingType} parking available`,
            restriction_description: i % 3 === 0 ? 'No overnight parking' : 'Maximum 24 hours',
            additionalProperties: {
                cleaned_price_zone: `Zone ${Math.floor(i / 100) + 1}`,
                free_duration_rules: i % 2 === 0 ? 'Free for 2 hours' : 'Free for 1 hour',
                cleaned_notes: `Additional notes for ${area} parking lot ${i}`,
                hourlyRate: Math.floor(Math.random() * 40) + 20,
                dailyRate: Math.floor(Math.random() * 200) + 100
            },
            feedback: Array.from({ length: Math.floor(Math.random() * 10) }, (_, j) => ({
                _id: `${i}_${j}`,
                userId: `user_${Math.floor(Math.random() * 1000)}`,
                feedbackType: Math.random() > 0.3 ? 'up' : 'down',
                comment: Math.random() > 0.7 ? `Comment ${j} for lot ${i}` : undefined,
                timestamp: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString()
            }))
        };
        
        totalParkingLots.push(parkingLot);
    }
    
    // Apply search filter
    let filteredLots = totalParkingLots;
    if (search) {
        const searchLower = search.toLowerCase();
        filteredLots = totalParkingLots.filter(lot => 
            lot.name.toLowerCase().includes(searchLower) ||
            (lot.address?.toLowerCase().includes(searchLower) || false) ||
            (lot.owner?.toLowerCase().includes(searchLower) || false) ||
            (lot.parking_type?.toLowerCase().includes(searchLower) || false)
        );
    }
    
    // Apply sorting
    filteredLots.sort((a, b) => {
        let aValue: any, bValue: any;
        
        switch (sortBy) {
            case 'name':
                aValue = a.name;
                bValue = b.name;
                break;
            case 'spaces':
                aValue = a.spaces || 0;
                bValue = b.spaces || 0;
                break;
            case 'parking_type':
                aValue = a.parking_type || '';
                bValue = b.parking_type || '';
                break;
            default:
                aValue = a.name;
                bValue = b.name;
        }
        
        if (typeof aValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
    });
    
    // Apply pagination
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedLots = filteredLots.slice(startIndex, endIndex);
    
    return {
        parkingLots: paginatedLots,
        pagination: {
            page,
            limit,
            total: filteredLots.length,
            pages: Math.ceil(filteredLots.length / limit)
        }
    };
};

export const mockDashboardData: DashboardOverview = {
    overview: {
        totalEvents: 532,
        todayEvents: 15,
        totalUniqueUsers: 57,
        todayUniqueUsers: 8,
        lastUpdated: new Date().toISOString()
    },
    lifecycle: {
        totalEvents: 294,
        todayEvents: 3,
        uniqueUsers: 59,
        todayUniqueUsers: 2,
        newUsersToday: 1,
        eventTypeBreakdown: {
            APP_LAUNCH: 160,
            FIRST_LAUNCH: 57
        },
        todayEventTypeBreakdown: {
            APP_LAUNCH: 2,
            FIRST_LAUNCH: 1
        }
    },
    search: {
        totalEvents: 187,
        todayEvents: 8,
        uniqueUsers: 45,
        todayUniqueUsers: 5,
        searchTypeBreakdown: {
            GENERAL: 120,
            CHEAP: 45,
            EASY: 22
        },
        todaySearchTypeBreakdown: {
            GENERAL: 5,
            CHEAP: 2,
            EASY: 1
        }
    },
    navigation: {
        totalEvents: 45,
        todayEvents: 2,
        uniqueUsers: 23,
        todayUniqueUsers: 1,
        navigationTypeBreakdown: {
            GOOGLE_MAPS: 30,
            APPLE_MAPS: 15
        },
        todayNavigationTypeBreakdown: {
            GOOGLE_MAPS: 1,
            APPLE_MAPS: 1
        }
    },
    share: {
        totalEvents: 3,
        todayEvents: 1,
        uniqueUsers: 2,
        todayUniqueUsers: 1,
        shareTypeBreakdown: {
            APP_STORE: 2,
            PARKING_SPOT: 1
        },
        todayShareTypeBreakdown: {
            APP_STORE: 1
        }
    },
    deeplink: {
        totalEvents: 3,
        todayEvents: 1,
        uniqueUsers: 0,
        todayUniqueUsers: 0,
        eventTypeBreakdown: {
            DEEPLINK_CLICKED: 3
        },
        todayEventTypeBreakdown: {
            DEEPLINK_CLICKED: 1
        },
        appConversionRate: 0.0
    }
};

export const mockParkingLots: ParkingLot[] = [
    {
        _id: '1',
        lotId: 'LOT001',
        name: 'Downtown Parking Garage',
        geometry: {
            type: 'Point',
            coordinates: [10.7522, 59.9139]
        },
        spaces: 150,
        parking_type: 'garage',
        address: 'Karl Johans gate 1, Oslo',
        owner: 'City of Oslo',
        notes: '24/7 parking available',
        restriction_description: 'No overnight parking',
        additionalProperties: {
            hourlyRate: 25,
            dailyRate: 150
        },
        feedback: []
    },
    {
        _id: '2',
        lotId: 'LOT002',
        name: 'Central Station Parking',
        geometry: {
            type: 'Point',
            coordinates: [10.7525, 59.9115]
        },
        spaces: 200,
        parking_type: 'underground',
        address: 'Jernbanetorget 1, Oslo',
        owner: 'Bane NOR',
        notes: 'Connected to Oslo Central Station',
        restriction_description: 'Maximum 24 hours',
        additionalProperties: {
            hourlyRate: 30,
            dailyRate: 180
        },
        feedback: []
    },
    {
        _id: '3',
        lotId: 'LOT003',
        name: 'Aker Brygge Parking',
        geometry: {
            type: 'Point',
            coordinates: [10.7315, 59.9132]
        },
        spaces: 80,
        parking_type: 'surface',
        address: 'Aker Brygge 1, Oslo',
        owner: 'Aker Brygge AS',
        notes: 'Waterfront parking',
        restriction_description: 'No heavy vehicles',
        additionalProperties: {
            hourlyRate: 35,
            dailyRate: 200
        },
        feedback: []
    }
];

export const mockParkingLotStats: ParkingLotStats = {
    total: 1500,
    withFeedback: 1200,
    uniqueUsersCount: 456,
    averageRating: '4.2',
    searchCount: 1234,
    navigationCount: 567,
    feedbackStats: {
        total: 89,
        positive: 67,
        negative: 12,
        comments: 10
    }
};

// Feedback Analytics Mock Data
export const mockFeedbackSummary: FeedbackSummary = {
    totalFeedback: 89,
    positiveFeedback: 67,
    negativeFeedback: 12,
    comments: 10,
    todayFeedback: 8,
    todayPositive: 6,
    todayNegative: 1,
    todayComments: 1,
    uniqueUsers: 45,
    todayUniqueUsers: 7
};

export const mockFeedbackActivity: FeedbackActivityResponse = {
    feedback: [
        {
            _id: '1',
            userId: 'user123',
            feedbackType: 'up',
            parkingLotId: 'LOT001',
            parkingLotName: 'Downtown Parking Garage',
            spotAddress: 'Karl Johans gate 1, Oslo',
            timestamp: '2024-01-15T10:30:00Z'
        },
        {
            _id: '2',
            userId: 'user456',
            feedbackType: 'comment',
            comment: 'Great parking spot, easy to find!',
            parkingLotId: 'LOT002',
            parkingLotName: 'Central Station Parking',
            spotAddress: 'Jernbanetorget 1, Oslo',
            timestamp: '2024-01-15T09:15:00Z'
        },
        {
            _id: '3',
            userId: 'user789',
            feedbackType: 'down',
            parkingLotId: 'LOT003',
            parkingLotName: 'Aker Brygge Parking',
            spotAddress: 'Aker Brygge 1, Oslo',
            timestamp: '2024-01-15T08:45:00Z'
        },
        {
            _id: '4',
            userId: 'user123',
            feedbackType: 'up',
            parkingLotId: 'LOT001',
            parkingLotName: 'Downtown Parking Garage',
            spotAddress: 'Karl Johans gate 1, Oslo',
            timestamp: '2024-01-14T16:20:00Z'
        },
        {
            _id: '5',
            userId: 'user456',
            feedbackType: 'comment',
            comment: 'Convenient location but expensive',
            parkingLotId: 'LOT002',
            parkingLotName: 'Central Station Parking',
            spotAddress: 'Jernbanetorget 1, Oslo',
            timestamp: '2024-01-14T14:30:00Z'
        }
    ],
    feedbackCounts: {
        up: 67,
        down: 12,
        comment: 10,
        total: 89
    },
    pagination: {
        page: 1,
        limit: 50,
        total: 89,
        pages: 2,
        hasNext: true,
        hasPrev: false
    },
    filters: {
        feedbackType: null,
        userId: null,
        startDate: null,
        endDate: null
    }
};
